// Markup drawing + persistence on the Cesium globe.
// Tools: pin (1 click), polyline / polygon (click points, right-click to finish),
// measure (2 clicks, geodesic distance). Geometry stored as GeoJSON WGS84 [lon,lat,h]
// with the camera state, so any markup can restore its authoring view.
import {
  Viewer,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Cartesian2,
  Cartesian3,
  Cartographic,
  Color,
  Entity,
  CallbackProperty,
  PolygonHierarchy,
  HeightReference,
  VerticalOrigin,
  LabelStyle,
  Math as CMath,
  PolylineDashMaterialProperty,
} from "cesium";
import type { CameraState, MarkupOut } from "../api";

export type MarkupTool = "none" | "pin" | "polyline" | "polygon" | "measure" | "freehand";

const MARKUP_COLOR = Color.fromCssColorString("#ff4d4d");
const MEASURE_COLOR = Color.fromCssColorString("#ffd24d");

// Sign-off palette for hand markup (matches the layer RAG colours).
export const MARKUP_COLORS = [
  { css: "#ff4d4d", name: "Red - problem" },
  { css: "#22C55E", name: "Green - good" },
  { css: "#ffd24d", name: "Amber - check" },
  { css: "#3aa0ff", name: "Blue - note" },
];

// Freehand stroke thickness: thin for writing, thick for shading an area.
export const MARKUP_WIDTHS = [
  { px: 4, name: "Thin" },
  { px: 8, name: "Medium" },
  { px: 16, name: "Thick" },
];

/** Markup must always read on top of meshes/terrain. Where a line passes BEHIND
 * geometry, render it dashed (the CAD hidden-line convention) instead of clipping. */
function alwaysVisibleLine(color: Color) {
  return {
    width: 3,
    material: color,
    depthFailMaterial: new PolylineDashMaterialProperty({
      color: color.withAlpha(0.85),
      dashLength: 12,
    }),
  };
}

// Freehand / area strokes: SOLID (not dashed) at the chosen thickness so colouring an
// area or writing reads clearly. Stays visible through terrain via a solid depth-fail.
function solidLine(color: Color, width: number) {
  return {
    width,
    material: color,
    depthFailMaterial: color.withAlpha(0.9),
    clampToGround: false,
  };
}

export interface DraftResult {
  type: Exclude<MarkupTool, "none">;
  geometry: { type: string; coordinates: unknown };
  label?: string;
  color: string;
  width?: number;
}

export class MarkupManager {
  private viewer: Viewer;
  private handler: ScreenSpaceEventHandler | null = null;
  private tool: MarkupTool = "none";
  private colorCss = "#ff4d4d";
  private penWidth = 8;
  private draftPositions: Cartesian3[] = [];
  private draftEntities: Entity[] = [];
  // Freehand session: strokes accumulate into ONE markup until the user clicks Done.
  private freehandStrokes: number[][][] = [];
  private pendingStrokes: Entity[] = [];
  private persisted = new Map<number, Entity[]>();
  private entityToMarkup = new Map<string, MarkupOut>();
  private viewHandler: ScreenSpaceEventHandler;
  // Freehand pen state.
  private penDown = false;
  private lastScreen: Cartesian2 | null = null;
  /** Called when a drawing gesture completes; host shows the save form. */
  onDraftComplete: (draft: DraftResult) => void = () => {};
  /** Called as freehand strokes accumulate, so the UI can show the count / enable Done. */
  onFreehandChange: (count: number) => void = () => {};
  onToolChange: (tool: MarkupTool) => void = () => {};
  /** Called when the user clicks a saved markup on the globe. */
  onMarkupSelect: (markup: MarkupOut) => void = () => {};

  setColor(css: string): void {
    this.colorCss = css;
  }
  getColor(): string {
    return this.colorCss;
  }
  setPenWidth(px: number): void {
    this.penWidth = px;
  }
  getPenWidth(): number {
    return this.penWidth;
  }
  private activeColor(): Color {
    return this.tool === "measure" ? MEASURE_COLOR : Color.fromCssColorString(this.colorCss);
  }

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    // Always-on click-to-view: when NOT drawing, clicking a saved markup opens it.
    this.viewHandler = new ScreenSpaceEventHandler(viewer.canvas);
    this.viewHandler.setInputAction((e: { position: Cartesian2 }) => {
      if (this.tool !== "none") return; // drawing handler owns clicks while armed
      const picked = this.viewer.scene.pick(e.position);
      const entity = picked?.id;
      if (entity instanceof Entity) {
        const m = this.entityToMarkup.get(entity.id);
        if (m) this.onMarkupSelect(m);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  setTool(tool: MarkupTool): void {
    this.cancelDraft();
    // Leaving freehand without clicking Done discards the accumulated strokes.
    if (this.tool === "freehand" && tool !== "freehand") this.clearFreehandPending();
    this.tool = tool;
    this.onToolChange(tool);
    if (tool === "none") {
      this.detach();
      this.viewer.canvas.style.cursor = "";
      return;
    }
    this.viewer.canvas.style.cursor = "crosshair";
    this.attach();
  }

  getTool(): MarkupTool {
    return this.tool;
  }

  cameraState(): CameraState {
    const c = this.viewer.camera;
    const pos = c.positionCartographic;
    return {
      lon: CMath.toDegrees(pos.longitude),
      lat: CMath.toDegrees(pos.latitude),
      height: pos.height,
      heading: c.heading,
      pitch: c.pitch,
      roll: c.roll,
    };
  }

  restoreCamera(s: CameraState): void {
    this.viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(s.lon, s.lat, s.height),
      orientation: { heading: s.heading, pitch: s.pitch, roll: s.roll },
      duration: 1.0,
    });
  }

  private escListener = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.cancelDraft();
      this.setTool("none");
    }
  };

  private winMouseUp = (): void => this.penEnd();

  private attach(): void {
    if (this.handler) return;
    this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);
    // Point-tools use clicks; the pen uses press-drag-release.
    this.handler.setInputAction((e: { position: Cartesian2 }) => {
      if (this.tool !== "freehand") this.click(e.position);
    }, ScreenSpaceEventType.LEFT_CLICK);
    this.handler.setInputAction((e: { position: Cartesian2 }) => {
      if (this.tool === "freehand") this.penStart(e.position);
    }, ScreenSpaceEventType.LEFT_DOWN);
    this.handler.setInputAction((e: { endPosition: Cartesian2 }) => {
      if (this.tool === "freehand") this.penMove(e.endPosition);
    }, ScreenSpaceEventType.MOUSE_MOVE);
    this.handler.setInputAction(() => {
      if (this.tool === "freehand") this.penEnd();
    }, ScreenSpaceEventType.LEFT_UP);
    this.handler.setInputAction(() => {
      if (this.tool !== "freehand") this.finishDraft();
    }, ScreenSpaceEventType.RIGHT_CLICK);
    document.addEventListener("keydown", this.escListener);
    window.addEventListener("mouseup", this.winMouseUp); // catch release off-canvas
  }

  private detach(): void {
    this.handler?.destroy();
    this.handler = null;
    this.releaseCamera();
    document.removeEventListener("keydown", this.escListener);
    window.removeEventListener("mouseup", this.winMouseUp);
  }

  private lockCamera(): void {
    this.viewer.scene.screenSpaceCameraController.enableInputs = false;
  }
  private releaseCamera(): void {
    this.viewer.scene.screenSpaceCameraController.enableInputs = true;
  }

  // --- freehand pen: drag draws a continuous stroke, released stroke auto-saves ---
  private penStart(screen: Cartesian2): void {
    const pos = this.pick(screen);
    if (!pos) return;
    this.penDown = true;
    this.lastScreen = screen.clone();
    this.draftPositions = [pos];
    this.lockCamera(); // so the drag draws instead of spinning the globe
    const color = this.activeColor();
    const entity = new Entity({
      polyline: {
        positions: new CallbackProperty(
          () => (this.draftPositions.length >= 2 ? [...this.draftPositions] : undefined),
          false,
        ) as never,
        ...solidLine(color, this.penWidth),
      },
    });
    this.viewer.entities.add(entity);
    this.draftEntities.push(entity);
  }

  private penMove(screen: Cartesian2): void {
    if (!this.penDown) return;
    if (this.lastScreen && Cartesian2.distance(screen, this.lastScreen) < 4) return; // thin out
    this.lastScreen = screen.clone();
    const pos = this.pick(screen);
    if (pos) {
      this.draftPositions.push(pos);
      this.viewer.scene.requestRender();
    }
  }

  private penEnd(): void {
    if (!this.penDown) return;
    this.penDown = false;
    this.releaseCamera();
    const pts = this.draftPositions.map((p) => {
      const c = Cartographic.fromCartesian(p);
      return [CMath.toDegrees(c.longitude), CMath.toDegrees(c.latitude), c.height];
    });
    this.draftEntities.forEach((e) => this.viewer.entities.remove(e));
    this.draftEntities = [];
    this.draftPositions = [];
    if (pts.length >= 2) {
      // Accumulate the stroke and keep it on screen as a solid pending line. All strokes
      // become ONE markup when the user clicks Done (finishFreehand), not one each.
      this.freehandStrokes.push(pts);
      const positions = Cartesian3.fromDegreesArrayHeights(pts.flatMap((c) => [c[0], c[1], c[2] ?? 0]));
      const e = this.viewer.entities.add({
        polyline: { positions, ...solidLine(this.activeColor(), this.penWidth) },
      });
      this.pendingStrokes.push(e);
      this.onFreehandChange(this.freehandStrokes.length);
      this.viewer.scene.requestRender();
    }
  }

  /** Bundle every accumulated freehand stroke into ONE markup and open the comment form. */
  finishFreehand(): void {
    if (!this.freehandStrokes.length) {
      this.setTool("none");
      return;
    }
    const draft: DraftResult = {
      type: "freehand",
      geometry: { type: "MultiLineString", coordinates: this.freehandStrokes },
      color: this.colorCss,
      width: this.penWidth,
    };
    this.clearFreehandPending();
    this.setTool("none");
    this.onDraftComplete(draft);
  }

  private clearFreehandPending(): void {
    this.pendingStrokes.forEach((e) => this.viewer.entities.remove(e));
    this.pendingStrokes = [];
    this.freehandStrokes = [];
    this.onFreehandChange(0);
  }

  private pick(screen: Cartesian2): Cartesian3 | null {
    const scene = this.viewer.scene;
    if (scene.pickPositionSupported) {
      const picked = scene.pickPosition(screen);
      if (picked) return picked;
    }
    return this.viewer.camera.pickEllipsoid(screen) ?? null;
  }

  private click(screen: Cartesian2): void {
    const pos = this.pick(screen);
    if (!pos) return;
    this.draftPositions.push(pos);

    if (this.tool === "pin") return this.finishDraft();
    if (this.tool === "measure" && this.draftPositions.length === 2) return this.finishDraft();

    if (this.draftPositions.length === 1) {
      // Live preview while drawing. The fill needs a PolygonHierarchy and the outline
      // needs a Cartesian3[] - they MUST be separate CallbackProperties. Sharing one
      // fed PolygonHierarchy into the polyline and crashed the renderer with
      // "cartesian is required". Return undefined while below the minimum point count.
      const linePositions = new CallbackProperty(
        () => (this.draftPositions.length >= 2 ? [...this.draftPositions] : undefined),
        false,
      );
      const previewColor = this.activeColor();
      const entity = new Entity(
        this.tool === "polygon"
          ? {
              polygon: {
                hierarchy: new CallbackProperty(
                  () =>
                    this.draftPositions.length >= 3
                      ? new PolygonHierarchy([...this.draftPositions])
                      : undefined,
                  false,
                ) as never,
                material: previewColor.withAlpha(0.3),
                perPositionHeight: true,
              },
              polyline: { positions: linePositions as never, ...alwaysVisibleLine(previewColor) },
            }
          : { polyline: { positions: linePositions as never, clampToGround: false, ...alwaysVisibleLine(previewColor) } },
      );
      this.viewer.entities.add(entity);
      this.draftEntities.push(entity);
    }
    this.viewer.scene.requestRender(); // render-on-demand: refresh the click preview
  }

  private finishDraft(): void {
    const tool = this.tool;
    const pts = this.draftPositions.map((p) => {
      const c = Cartographic.fromCartesian(p);
      return [CMath.toDegrees(c.longitude), CMath.toDegrees(c.latitude), c.height];
    });
    const min =
      tool === "pin" ? 1 : tool === "measure" ? 2 : tool === "polygon" ? 3 : 2;
    if (pts.length < min) return this.cancelDraft();

    const color = tool === "measure" ? "#ffd24d" : this.colorCss;
    let draft: DraftResult;
    if (tool === "pin") {
      draft = { type: "pin", geometry: { type: "Point", coordinates: pts[0] }, color };
    } else if (tool === "polygon") {
      draft = {
        type: "polygon",
        geometry: { type: "Polygon", coordinates: [[...pts, pts[0]]] },
        color,
      };
    } else {
      draft = { type: tool as "polyline" | "measure", geometry: { type: "LineString", coordinates: pts }, color };
      if (tool === "measure") {
        const dist = this.distance(this.draftPositions[0], this.draftPositions[1]);
        draft.label = `${dist.toFixed(dist < 100 ? 2 : 1)} m`;
      }
    }
    this.cancelDraft();
    this.setTool("none");
    this.onDraftComplete(draft);
  }

  private distance(a: Cartesian3, b: Cartesian3): number {
    return Cartesian3.distance(a, b);
  }

  cancelDraft(): void {
    this.draftEntities.forEach((e) => this.viewer.entities.remove(e));
    this.draftEntities = [];
    this.draftPositions = [];
    if (this.penDown) {
      this.penDown = false;
      this.releaseCamera();
    }
  }

  /** Draw (or redraw) all persisted markups. */
  showMarkups(markups: MarkupOut[]): void {
    for (const entities of this.persisted.values()) {
      entities.forEach((e) => this.viewer.entities.remove(e));
    }
    this.persisted.clear();
    this.entityToMarkup.clear();
    for (const m of markups) {
      this.addPersisted(m);
      for (const e of this.persisted.get(m.id) ?? []) {
        this.entityToMarkup.set(e.id, m);
      }
    }
    this.viewer.scene.requestRender();
  }

  private addPersisted(m: MarkupOut): void {
    if (!m.geometry) return;
    const entities: Entity[] = [];
    const styleColor = (m.style as { color?: string } | null)?.color;
    const color = styleColor
      ? Color.fromCssColorString(styleColor)
      : m.type === "measure"
        ? MEASURE_COLOR
        : MARKUP_COLOR;
    const labelText = m.label || m.comment || "";

    if (m.geometry.type === "Point") {
      const [lon, lat, h] = m.geometry.coordinates as number[];
      entities.push(
        this.viewer.entities.add({
          position: Cartesian3.fromDegrees(lon, lat, h ?? 0),
          point: {
            pixelSize: 11, color, outlineColor: Color.WHITE, outlineWidth: 2,
            heightReference: HeightReference.NONE, disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          label: labelText
            ? {
                text: labelText.slice(0, 48),
                font: "13px sans-serif",
                pixelOffset: new Cartesian2(0, -20),
                fillColor: Color.WHITE,
                style: LabelStyle.FILL_AND_OUTLINE,
                outlineColor: Color.BLACK,
                outlineWidth: 3,
                verticalOrigin: VerticalOrigin.BOTTOM,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
              }
            : undefined,
        }),
      );
    } else if (m.geometry.type === "LineString") {
      const coords = (m.geometry.coordinates as number[][]).flatMap((c) => [c[0], c[1], c[2] ?? 0]);
      const positions = Cartesian3.fromDegreesArrayHeights(coords);
      entities.push(
        this.viewer.entities.add({
          polyline: { positions, ...alwaysVisibleLine(color) },
        }),
      );
      if (labelText) {
        const mid = positions[Math.floor(positions.length / 2)];
        entities.push(
          this.viewer.entities.add({
            position: mid,
            label: {
              text: labelText.slice(0, 48),
              font: "13px sans-serif",
              fillColor: Color.WHITE,
              style: LabelStyle.FILL_AND_OUTLINE,
              outlineColor: Color.BLACK,
              outlineWidth: 3,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
          }),
        );
      }
    } else if (m.geometry.type === "MultiLineString") {
      // Freehand: many strokes, one markup. Solid line at the saved thickness.
      const width = (m.style as { width?: number } | null)?.width ?? 8;
      const strokes = m.geometry.coordinates as number[][][];
      for (const stroke of strokes) {
        if (stroke.length < 2) continue;
        const positions = Cartesian3.fromDegreesArrayHeights(stroke.flatMap((c) => [c[0], c[1], c[2] ?? 0]));
        entities.push(
          this.viewer.entities.add({ polyline: { positions, ...solidLine(color, width) } }),
        );
      }
      if (labelText && strokes[0]?.length) {
        const first = Cartesian3.fromDegreesArrayHeights(strokes[0].flatMap((c) => [c[0], c[1], c[2] ?? 0]));
        entities.push(
          this.viewer.entities.add({
            position: first[Math.floor(first.length / 2)],
            label: {
              text: labelText.slice(0, 48), font: "13px sans-serif", fillColor: Color.WHITE,
              style: LabelStyle.FILL_AND_OUTLINE, outlineColor: Color.BLACK, outlineWidth: 3,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
          }),
        );
      }
    } else if (m.geometry.type === "Polygon") {
      const ring = (m.geometry.coordinates as number[][][])[0];
      const positions = Cartesian3.fromDegreesArrayHeights(ring.flatMap((c) => [c[0], c[1], c[2] ?? 0]));
      // Fill can be occluded by meshes, but the boundary must always read: draw the
      // ring as a separate always-visible polyline (dashed where hidden).
      entities.push(
        this.viewer.entities.add({
          polygon: {
            hierarchy: new PolygonHierarchy(positions),
            material: color.withAlpha(0.25),
            perPositionHeight: true,
          },
        }),
        this.viewer.entities.add({
          polyline: { positions, ...alwaysVisibleLine(color) },
        }),
      );
    }
    this.persisted.set(m.id, entities);
  }
}
